import { UploaderError } from '@contember/bindx-uploader'

/**
 * Shared upload plumbing for `ImageField` / `FileField` (was `image-path-field.tsx`).
 * Files land in S3 under `assets/` — the only prefix the `admin` role may
 * upload to (`packages/api/model/acl.ts`).
 */
export const UPLOAD_PREFIX = 'assets'

// Raster + vector formats every current browser can render inline. HEIC/HEIF,
// TIFF, BMP and friends are intentionally excluded — they slip past a naive
// `image/*` check and show as a broken image on the web.
export const ALLOWED_IMAGE_FORMATS = [
	{ mime: 'image/jpeg', extensions: ['.jpg', '.jpeg'], label: 'JPG' },
	{ mime: 'image/png', extensions: ['.png'], label: 'PNG' },
	{ mime: 'image/webp', extensions: ['.webp'], label: 'WebP' },
	{ mime: 'image/gif', extensions: ['.gif'], label: 'GIF' },
	{ mime: 'image/avif', extensions: ['.avif'], label: 'AVIF' },
	{ mime: 'image/svg+xml', extensions: ['.svg'], label: 'SVG' },
] as const

export const ALLOWED_IMAGE_LABELS = ALLOWED_IMAGE_FORMATS.map(format => format.label).join(', ')
export const ALLOWED_IMAGE_ACCEPT = ALLOWED_IMAGE_FORMATS
	.flatMap(format => [format.mime, ...format.extensions])
	.join(',')

/** Documents the `FileField` accepts (PDF first — brochures, price lists). */
export const ALLOWED_FILE_ACCEPT = '.pdf,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip'

export function isRenderableImage(file: File): boolean {
	const mime = file.type.trim().toLowerCase()
	if (mime) return ALLOWED_IMAGE_FORMATS.some(format => format.mime === mime)
	// No MIME (common for HEIC and some exotic types) — decide by extension.
	const name = file.name.toLowerCase()
	return ALLOWED_IMAGE_FORMATS.some(format => format.extensions.some(extension => name.endsWith(extension)))
}

export function getUploadErrorMessage(error: unknown, fallback = 'Could not upload the file.'): string {
	if (error instanceof UploaderError) {
		return error.options.endUserMessage ?? fallback
	}
	if (error instanceof Error) {
		return error.message
	}
	return fallback
}

export type ImageDimensions = { width: number; height: number }

/**
 * Intrinsic pixel size of an image file. `createImageBitmap` is the fast path;
 * SVG (and some browsers for AVIF) fall back to an `<img>` decode. `null`
 * when neither works — the row is still created, just without dimensions.
 */
export async function readImageDimensions(file: File): Promise<ImageDimensions | null> {
	if (typeof createImageBitmap === 'function') {
		try {
			const bitmap = await createImageBitmap(file)
			const size = { width: bitmap.width, height: bitmap.height }
			bitmap.close()
			if (size.width > 0 && size.height > 0) return size
		} catch {
			// fall through to the <img> path
		}
	}
	if (typeof Image === 'undefined' || typeof URL.createObjectURL !== 'function') return null
	return new Promise(resolve => {
		const url = URL.createObjectURL(file)
		const image = new Image()
		image.onload = () => {
			URL.revokeObjectURL(url)
			resolve(image.naturalWidth > 0 ? { width: image.naturalWidth, height: image.naturalHeight } : null)
		}
		image.onerror = () => {
			URL.revokeObjectURL(url)
			resolve(null)
		}
		image.src = url
	})
}

export function formatFileSize(bytes: number | null): string {
	if (bytes == null) return ''
	if (bytes < 1024) return `${bytes} B`
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
